import { DateTime } from 'luxon';
import {
  GenderEnum,
  IFileType,
  ILinkTaskTypeEnum,
  RPMCohortViewColorsEnum,
} from './generic.types';
import type {
  IBloodPressureTimeInZoneChartData,
  INotesWidgetNote,
  IPatientAdherenceWidgetData,
  IPatientCommunicationWidgetHealthCareProvider,
  IPatientRecommendations,
  IRPMAlert,
  IRPMPatientMedications,
  IRPMPatientName,
  ITemperatureData,
  ITriageStatus,
  PatientBloodPressureCategoryEnum,
} from './patients.types';
import type { ISettingsTagsCategory } from './views-types';
import { IAssignedClinician } from './clinicians.types';

export interface IRpmPatientStepsMeasurement {
  timestamp: string;
  value: number;
}

export interface IRpmPatientSteps {
  average: number;
  max: number;
  min: number;
  measurements: IRpmPatientStepsMeasurement[];
}

export interface IRpmPatientWeightMeasurement {
  timestamp: string;
  value: number;
  provenance: ProvenanceEnum;
}

export interface IWeightChartMeasurement {
  value: number;
  date: DateTime;
}

export interface IDailyWeightChartMeasurement {
  max: number;
  min: number;
  date: DateTime;
}

export interface IChartTrend {
  category: 'INCREASE' | 'DECREASE' | 'SAME' | 'INSUFFICIENT';
  change: number;
}

export interface IRpmPatientWeight {
  fluctuation: number;
  max: number;
  measurements: IRpmPatientWeightMeasurement[];
  min: number;
  reference: number;
  trend?: IChartTrend;
}

export interface IMacroNutrients {
  calories: number;
  carbohydrates: number;
  fat: number;
  fiber: number;
  protein: number;
  saturatedFat: number;
  sodium: number;
  sugar: number;
}

export interface IFoodItem {
  name: string;
  macros: IMacroNutrients;
}

export interface INutritionDay {
  timestamp: string; // ISO date string
  macros: IMacroNutrients;
  food: IFoodItem[];
}

export interface IRpmPatientNutrition {
  days: INutritionDay[];
}

export interface IRpmPatientInformationAdherenceMeasurement {
  timestamp: string;
  value: number;
}

export interface IRpmPatientInformationAdherenceTrend {
  category: IChartTrend['category'];
  change: number;
}

export interface IRpmPatientInformationAdherenceItem {
  average: number;
  difference: number;
  text: string;
  color: string | RPMCohortViewColorsEnum;
  measurements: IRpmPatientInformationAdherenceMeasurement[];
  trend?: IRpmPatientInformationAdherenceTrend;
}

export interface IRpmPatientInformationAdherence {
  aggregate: IRpmPatientInformationAdherenceItem;
  glucose: IRpmPatientInformationAdherenceItem;
  insulin: IRpmPatientInformationAdherenceItem;
  medication: IRpmPatientInformationAdherenceItem;
  weight: IRpmPatientInformationAdherenceItem;
  bloodPressure: IRpmPatientInformationAdherenceItem;
  activity: IRpmPatientInformationAdherenceItem;
}

export interface IRpmPatientInformationEngagementMeasurement {
  time: string;
  engaged: boolean;
}

export interface IRpmPatientInformationEngagement {
  average: number;
  measurements: IRpmPatientInformationEngagementMeasurement[];
}

export enum ProvenanceEnum {
  BLUETOOTH = 'bluetooth',
  MANUAL = 'manual',
  CLOUD = 'cloud',
  CELLULAR = 'cellular',
  NO_PROVENANCE = '',
}

export enum SymptomSeverityEnum {
  MILD = 'mild',
  MODERATE = 'moderate',
  SEVERE = 'severe',
}

export type SymptomsMap = {
  [key: string]: Array<ISymptomMeasurement | null>;
};

export interface ISymptomMeasurement {
  symptoms: ISymptomInformation[];
  maxDaySeverity: SymptomSeverityEnum;
  date: DateTime;
}

export interface ISymptomInformation {
  severity: SymptomSeverityEnum;
  symptom: string;
  timestamp: string;
}

export interface IAppEngagementMeasurement {
  color: RPMCohortViewColorsEnum | string;
  timestamp: string | null;
}

export interface IAppEngagementComponent {
  name: string;
  timestamp: string;
  color?: string;
}
export interface IAppEngagement {
  weight: IAppEngagementMeasurement;
  activity: IAppEngagementMeasurement;
  bloodGlucose: IAppEngagementMeasurement;
  insulin: IAppEngagementMeasurement;
  latest?: IAppEngagementMeasurement;
}
export interface IRpmPatientInformationApplicationInformation {
  engagement: IAppEngagement;
  platform: string;
  details: string;
  timestamp: string;
  version: string;
}

export interface IRpmMessagesObject {
  unreadMessages: number;
}

export interface IRpmContinuousGlucoseMeasurement {
  color: RPMCohortViewColorsEnum;
  hasEnoughData?: boolean;
  provenance: ProvenanceEnum;
  timeBelowRange?: number;
  timeInRange?: number;
  timestamp: string; // Datetime ISO string
  value: number;
}

export interface IRpmContinuousGlucoseMeasurementMaxMinInfo {
  max: IRpmContinuousGlucoseMeasurement;
  min: IRpmContinuousGlucoseMeasurement;
}

export interface IRpmContinuousGlucoseMeasurementsInformation {
  average: IRpmGlucoseAverage;
  equivalentA1CAverage: number;
  gmi?: { trend?: IChartTrend; value: number };
  measurements: IRpmContinuousGlucoseMeasurement[];
  thresholds: IRpmGlucoseThresholds;
  timeInRange?: { trend?: IChartTrend; value: number };
  zonalChart: IZonalChartValues;
}

export interface IRpmGlucoseAverage {
  color: RPMCohortViewColorsEnum;
  trend?: IChartTrend;
  value: number;
}

export interface IRpmGlucoseThresholds {
  criticalHigh: number;
  criticalLow: number;
  high: number;
  low: number;
}

export interface IRpmBloodGlucoseMeasurementsInformation {
  average: IRpmGlucoseAverage;
  equivalentA1CAverage: number;
  measurements: IRpmBloodGlucoseInformation[];
  thresholds: IRpmGlucoseThresholds;
  zonalChart: IZonalChartValues;
}

export interface IHeartRateMetric {
  color: RPMCohortViewColorsEnum;
  trend?: IChartTrend;
  value: number;
}

export interface IHeartRateMeasurementsInformation {
  average: IHeartRateMetric;
  max: IHeartRateMetric;
  min: IHeartRateMetric;
  values: IHeartRateValue[];
}

export interface IHeartIrregularRateMeasurementsInformation {
  values: IHeartIrregularRateValue[];
}

export interface IHeartRestingMeasurementsInformation {
  [key: string]: IHeartRateMeasurementsInformation;
}

export type IHeartRestingMeasurementsInformationResponse = {
  thresholds: IRpmGlucoseThresholds;
} & {
  [key: string]: IHeartRateMeasurementsInformation;
};

export interface IHeartRateValue {
  provenance: ProvenanceEnum;
  timestamp: string;
  value: number;
  color?: RPMCohortViewColorsEnum;
  displayedValue?: string;
}

export interface IDailyHeartRateHourlyValues {
  average: number;
  min: number;
  max: number;
}

export interface IHeartIrregularRateValue {
  provenance: ProvenanceEnum;
  timestamp: string;
  value: boolean;
}

export interface IMeasurementValue {
  provenance: ProvenanceEnum | string;
  timestamp: string;
}

export interface IHeartRate {
  active?: IHeartRateMeasurementsInformation;
  irregular?: IHeartIrregularRateMeasurementsInformation;
  resting?: IHeartRestingMeasurementsInformation;
  thresholds?: IRpmGlucoseThresholds;
}

export interface ITimeline {
  action: string;
  details: string;
  carePlanTitle: string;
  taskId: string;
  taskTitle: string;
  timestamp: string;
  cohortId?: string;
  cohortName: string;
}

export interface ITimelineToShow extends ITimeline {
  marginLeft: number;
  show: boolean;
  totalWithSameWidth: ITimeline[];
}

export interface ITimelineResponse {
  timeline?: ITimeline[];
}

export interface IRpmPatientTags {
  categories: ISettingsTagsCategory[];
  cohort: IRpmTagsArray;
  patient: IRpmTagsArray;
}

export interface IRpmTagsArray {
  tags: string[];
}

export interface IRpmPatientInformation {
  adherence: IRpmPatientInformationAdherence | IPatientAdherenceWidgetData;
  app: IRpmPatientInformationApplicationInformation;
  bloodPressure: IRpmBloodPressureInformation;
  bgm: IRpmBloodGlucoseMeasurementsInformation;
  cgm: IRpmContinuousGlucoseMeasurementsInformation;
  engagement: IRpmPatientInformationEngagement;
  heartrate: IHeartRate;
  insulin: IRpmInsulinInformation;
  medications: IRPMPatientMedications;
  patient: IRpmPatientSummary;
  notes?: INotesWidgetNote[];
  symptoms: ISymptomInformation[];
  steps: IRpmPatientSteps;
  tags: string[];
  tagCategories: ISettingsTagsCategory[];
  weight: IRpmPatientWeight;
  nutrition: IRpmPatientNutrition;
  messages: IRpmMessagesObject;
  recommendations: IPatientRecommendations;
  triageStatus?: ITriageStatus;
  temperature?: ITemperatureData;
  smartGoals?: ISmartGoalsData;
  lifestyle?: ILifestyleWidgetData;
  documents?: IDocumentsWidgetData;
}

export enum RPMBloodGlucoseInformationContextEnum {
  PREACTIVITY = 'pre-activity',
  POSTACTIVITY = 'post-activity',
  PREEVENT = 'pre-event',
  POSTEVENT = 'post-event',
  FASTING = 'fasting',
  POSTPRANDIAL = 'postprandial',
  PREPRANDIAL = 'preprandial',
  PRESLEEP = 'pre-sleep',
  POSTSLEEP = 'post-sleep',
  OTHER = 'other',
  UNKNOWN = 'unknown',
}

export interface IRpmBloodGlucoseInformation {
  color: RPMCohortViewColorsEnum;
  timestamp: string;
  context: RPMBloodGlucoseInformationContextEnum;
  value: number;
  provenance: ProvenanceEnum;
}

export interface IRpmBloodGlucoseDisplayInformation {
  max: number;
  min: number;
  colorMax: RPMCohortViewColorsEnum;
  colorMin: RPMCohortViewColorsEnum;
  provenance: ProvenanceEnum;
}

export interface IRpmCGMDisplayInformation {
  max: number;
  min: number;
  colorMax: RPMCohortViewColorsEnum;
  colorMin: RPMCohortViewColorsEnum;
}

export interface IRpmBloodPressureDisplayInformation {
  diastolicColor: RPMCohortViewColorsEnum | string;
  systolicColor: RPMCohortViewColorsEnum | string;
  maxDiastolic: number;
  minDiastolic: number;
  maxSystolic: number;
  minSystolic: number;
  provenance?: ProvenanceEnum;
}

export interface IRpmBloodPressureStruct {
  alert: IRPMAlert;
  value: number;
  category: PatientBloodPressureCategoryEnum | string;
}

export interface IRpmBloodPressureMeasurement {
  color: RPMCohortViewColorsEnum;
  timestamp: string;
  diastolic: IRpmBloodPressureStruct;
  systolic: IRpmBloodPressureStruct;
  provenance: ProvenanceEnum;
}

export interface IBloodPressureTimeSeriesData {
  timestamp: string;
  diastolic: IBloodPressureTimeSeriesMeasurement;
  systolic: IBloodPressureTimeSeriesMeasurement;
  provenance?: ProvenanceEnum;
}

export interface IBloodPressureTimeSeriesMeasurement {
  value: number;
  color: RPMCohortViewColorsEnum | string;
  category: PatientBloodPressureCategoryEnum | string;
}

export interface IBloodPressureMetric {
  color: RPMCohortViewColorsEnum;
  trend?: IChartTrend;
  value: number;
}

export interface IBloodPressureStats {
  average: IBloodPressureMetric;
  max: IBloodPressureMetric;
  min: IBloodPressureMetric;
}

export interface IRpmBloodPressureInformation {
  measurements: IRpmBloodPressureMeasurement[];
  systolic: IBloodPressureStats;
  diastolic: IBloodPressureStats;
  timeInZone: IBloodPressureTimeInZoneChartData;
  timeInRange?: { trend?: IChartTrend; value: number };
}

export interface IRpmInsulinDisplayInformation {
  totalBasal: number | null;
  totalBolus: number | null;
}

export enum RpmPopupInsulinTypeEnum {
  BASAL = 'basal',
  BOLUS = 'bolus',
}

export interface IRpmPopupInsulinDisplayInformation {
  value: number;
  timestamp: string;
  type: RpmPopupInsulinTypeEnum;
}

export interface IRpmInsulinInformation {
  basalBolusRatio: IRpmBasalBolusRatio;
  fastActing: IRpmInsulinActing[];
  longActing: IRpmInsulinActing[];
}

export interface IRpmBasalBolusRatio {
  basalPercent: number;
  bolusPercent: number;
}

export interface IRpmInsulinActing {
  timestamp: string;
  value: number;
  isBasal?: boolean;
}

export interface IZonalChartValues {
  low: number;
  high: number;
  veryHigh: number;
  veryLow: number;
  target: number;
}

export interface IRpmPatientSummary {
  birthday: string;
  careMode: string;
  clinicians: IAssignedClinician[];
  conditions: string[];
  email: string;
  lastSync?: string;
  mrn: string;
  name: IRPMPatientName;
  patientId: string;
  phone: string;
  phoneSecondary: string;
  pronouns: string;
  provider: IPatientCommunicationWidgetHealthCareProvider;
  sex: GenderEnum | string;
  tags: string[];
  triageStatus?: ITriageStatus;
  temperature?: ITemperatureData;
  userId: string;
  enrollmentDate?: string;
}

export interface ICareModeResponse {
  careMode: string;
}

export interface IConditionsResponse {
  conditions: string[];
}

export interface IRpmPatientUpdateFieldsResponse {
  mrn: string;
  phone: string;
  phoneSecondary: string;
  tags: string[];
  userId: string;
}

export interface IRpmPatientInformationPayload {
  cohortId?: string;
  patientId: string;
  startDate: DateTime;
  endDate: DateTime;
  charts?: string[];
  onlyCharts?: boolean;
  isInitializeDetailView?: boolean;
  isSyncPatient?: boolean;
}

export interface IRpmPatientCohort {
  cohortId?: string;
  patientId: string;
}

export interface IRPMClearPatientInformationPayload {
  isInitializeDetailView: boolean;
  isSyncPatient: boolean;
}

export interface ITimeTracking {
  date: string;
  seconds: number;
}

export interface IRPMPatientTasksPayload {
  patientId: string;
  carePlanId: string;
  carePlanTitle?: string;
  carePlanChanged?: boolean;
  clinicianTimezone?: string;
}

export interface IDropdownOption {
  value: number;
  id?: string | ILinkTaskTypeEnum;
  hidden?: boolean;
  timezoneName?: string;
  displayValue: string;
  url?: string;
  hourAndMinutes?: {
    hour: number;
    minutes: number;
  };
}

export interface ITimelineEvent {
  dateCreated: string;
  dateUpdated: string;
  icon: string;
  subtitle: string;
  title: string;
  type: TimelineEventTypeEnum;
}

export interface ITimelineGroupedEvents {
  [key: string]: ITimelineEvent[];
}

export interface ITimelineReviewer {
  firstName: string;
  lastName: string;
}

export interface ITimelineReviewed {
  reviewer: ITimelineReviewer;
  timestamp: string;
}

export interface IReviewedStatus {
  status: boolean;
}

export interface ITimelineEventsResponse {
  reviewed: ITimelineReviewed;
  timeline: ITimelineEvent[];
}

export enum TimelineEventTypeEnum {
  ACTIVITY = 'ACTIVITY',
  APPOINTMENT = 'APPOINTMENT',
  BLOOD_GLUCOSE = 'BLOOD_GLUCOSE',
  BLOOD_PRESSURE = 'BLOOD_PRESSURE',
  CGM = 'CGM',
  INSULIN = 'INSULIN',
  IRREGULAR_HEART_RHYTHM = 'IRREGULAR_HEART_RHYTHM',
  MEDICATION = 'MEDICATION',
  RESTING_HEART_RATE = 'RESTING_HEART_RATE',
  WEIGHT = 'WEIGHT',
  REVIEWED = 'REVIEWED',
  SURVEY = 'SURVEY',
  SYMPTOM = 'SYMPTOM',
  TRIAGE = 'TRIAGE',
  PATIENT_ASSISTANT_QUERY = 'PATIENT_ASSISTANT_QUERY',
  TEMPERATURE = 'TEMPERATURE',
  SMART_GOAL = 'SMART_GOAL',
}

export interface IMyWhyInfo {
  caption: string;
  dateCreated: string;
  image: IFileType;
}

export interface ILifestyleWidgetData {
  tasks: ILifestyleTaskWidgetData[];
}

export interface IDocumentsWidgetData {
  documents: IDocumentsWidgetItem[];
}

export interface IDocumentsWidgetItem {
  dateCreated: string;
  id: string;
  title: string;
  fileType?: string;
}

export interface IDocumentsDetailsWidgetItem {
  dateCreated: string;
  id: string;
  title: string;
  fileType: string;
  content?: ISignedUrlResponse;
}

export interface ICreateDocumentPayload {
  title: string;
  fileType: string;
  mimeType?: string;
  sizeBytes?: number;
}

export type SignedUrlMethod = 'GET' | 'PUT';

export interface ISignedUrlResponse {
  md5Hash?: string;
  method: SignedUrlMethod;
  signedUrl: string;
}

export interface ILifestyleTaskWidgetData {
  title: string;
  values: ILifestyleTaskDetailsWidgetData[];
}

export interface ILifestyleTaskDetailsWidgetData {
  timestamp: string;
  value: string;
}

export interface ISmartGoalsData {
  goals: ISmartGoal[];
}

export interface ISmartGoal {
  completed: boolean;
  end: string;
  instructions: string;
  title: string;
  start: string;
  tracked: string;
  weekdays: Record<WeekdayEnum, IGoalDay>;
}

export enum WeekdayEnum {
  Sunday = 'sunday',
  Monday = 'monday',
  Tuesday = 'tuesday',
  Wednesday = 'wednesday',
  Thursday = 'thursday',
  Friday = 'friday',
  Saturday = 'saturday',
}

export interface IGoalDay {
  completed: boolean;
  note: string;
  timestamp: string;
}

export interface ISmartGoalReportWeek {
  completed: number;
  end: string;
  start: string;
}

export interface ISmartGoalReportGoal {
  completed: number; // number of weeks the goal was fully achieved
  expected: number; // times per week the patient should complete the goal
  title: string;
  weeks: ISmartGoalReportWeek[];
}

export interface ISmartGoalReportData {
  smartGoals: ISmartGoalReportGoal[];
}
